import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

export default function LegalizacijaKucaSplitPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Tko može pokrenuti legalizaciju kuće u Splitu?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Postupak može pokrenuti vlasnik ili ovlašteni opunomoćenik uz osnovnu dokumentaciju o nekretnini.",
        },
      },
      {
        "@type": "Question",
        name: "Koliko traje legalizacija kuće?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tipično trajanje postupka je od 30 do 90 dana, ovisno o predmetu i nadležnom tijelu.",
        },
      },
      {
        "@type": "Question",
        name: "Radite li legalizaciju nekretnina u Zadar, Trogiru, Makarskoj?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Da. Radimo legalizaciju nekretnina na cijeloj Dalmaciji: od Zadra, Šibenika, Primoštena, Trogira, Kaštela, Splita, Omiša do Makarske i Podgore.",
        },
      },
      {
        "@type": "Question",
        name: "Što je potrebno za legalizaciju bespravno izgrađene kuće?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Potrebni su osnovni podaci vlasnika, katastarska oznaka, geodetska snimka izvedenog stanja, te arhitektonski nacrti objekta.",
        },
      },
    ],
  };

  return (
    <div data-testid="legalizacija-kuca-split-page">
      <Seo
        title="Legalizacija kuće | Dalmacija | Infokarta"
        description="Legalizacija bespravno izgrađene kuće na cijeloj Dalmaciji. Geodetski ured Infokarta radi u Zadru, Šibeniku, Trogiru, Splitu, Makarskoj i ostalim gradovima."
        path="/legalizacija-kuca"
        jsonLd={jsonLd}
      />

      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="overline text-ink-soft">Dalmacija</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
            Legalizacija<br />
            <span className="text-ink/40">kuće</span>
          </h1>
          <p className="text-ink-soft leading-relaxed max-w-3xl mt-8">
            Vodimo cijeli postupak legalizacije kuće na cijeloj Dalmaciji. Od početne provjere, geodetske i arhitektonske dokumentacije, 
            do predaje zahtjeva i završnog upisa u katastar i zemljišne knjige. Radimo na području od Zadra do Makarske.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
        <article className="md:col-span-8 space-y-6 text-ink-soft leading-relaxed">
          <h2 className="font-display text-3xl text-ink">Kako izgleda postupak</h2>
          <p>
            Nakon inicijalne analize stanja objekta pripremamo svu potrebnu dokumentaciju
            i vodimo predmet prema nadležnom upravnom tijelu. U svakom koraku dobivate
            jasan status i sljedeće aktivnosti.
          </p>
          <h2 className="font-display text-3xl text-ink">Što je najčešće potrebno</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>osnovni podaci o vlasniku i objektu</li>
            <li>lokacija i katastarska oznaka</li>
            <li>snimka izvedenog stanja i pripadajući elaborati</li>
          </ul>
        </article>

        <aside className="md:col-span-4 border border-hair p-6 bg-white h-fit">
          <div className="overline text-ink-soft">Sljedeći korak</div>
          <p className="text-ink-soft mt-3 text-sm leading-relaxed">
            Pošaljite osnovne podatke i javit ćemo vam procjenu izvedivosti i okvirni
            trošak postupka.
          </p>
          <Link
            to="/prijava"
            className="inline-flex items-center gap-2 mt-5 bg-ink text-white px-5 py-3 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors"
          >
            Pošaljite prijavu
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </aside>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20 border-t border-hair">
        <h2 className="font-display text-4xl text-ink mb-10">Legalizacija nekretnina od Zadra do Makarske</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            "Zadar",
            "Šibenik",
            "Primoštena",
            "Rogoznica",
            "Trogir",
            "Kaštela",
            "Split",
            "Podstrana",
            "Omiš",
            "Makarska",
            "Podgora",
          ].map((grad) => (
            <div key={grad} className="flex items-center gap-2 text-ink-soft">
              <span className="text-ink text-lg">→</span>
              <span className="text-sm">{grad}</span>
            </div>
          ))}
        </div>
        <p className="text-ink-soft leading-relaxed mt-8">
          Specijalizirani smo za legalizaciju bespravno izgrađenih objekata na cijeloj Dalmaciji — od Zadarskog primorja preko Split i Dalmatske zagore, 
          sve do južnodalmatinskih gradova. Bez obzira gdje se nalazi vaša kuća ili gospodarski objekt, znamo kako navigirati lokalnim jedinicama 
          i katastrom vašeg područja.
        </p>
      </section>
    </div>
  );
}